import { useState, useEffect, useCallback } from 'react';
import { FormState, CalculationResult, ClipboardState } from '../types';
import { calculateSampleSize } from '../../../utils/statistics';
import { copyToClipboard } from '../../../utils/clipboard';
import { createShareUrl, deserializeUrlToState } from '../../../utils/url';

export function useCalculatorForm() {
  // Default form values
  const [formState, setFormState] = useState<FormState>({
    testType: 'conversion',
    variants: 2, // Control + 1 variant
    baselineValue: 10, // 10% conversion rate by default
    standardDeviation: 5, // Only used for metric tests
    logTransformation: false, // Default to no log transformation
    mde: 5, // 5% relative improvement
    confidenceLevel: 95, // 95% confidence
    power: 80, // 80% power
    dailySamples: 1000, // 1000 samples per day
  });

  const [result, setResult] = useState<CalculationResult | null>(null);
  const [showStdDev, setShowStdDev] = useState(false);
  
  // Clipboard feedback state
  const [clipboardState, setClipboardState] = useState<ClipboardState>({
    samplesPerVariantCopied: false,
    totalSampleSizeCopied: false,
    urlCopied: false
  });

  // Initialize form state from URL parameters if available
  useEffect(() => {
    const urlState = deserializeUrlToState();
    if (urlState) {
      setFormState(prevState => ({
        ...prevState,
        ...urlState
      }));
    }
  }, []);

  // Reset clipboard feedback state after 2 seconds
  useEffect(() => {
    if (clipboardState.samplesPerVariantCopied || clipboardState.totalSampleSizeCopied || clipboardState.urlCopied) {
      const timer = setTimeout(() => {
        setClipboardState({
          samplesPerVariantCopied: false,
          totalSampleSizeCopied: false,
          urlCopied: false
        });
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [clipboardState]);

  // Update showStdDev when testType changes
  useEffect(() => {
    setShowStdDev(formState.testType === 'metric');
  }, [formState.testType]);

  // Validate form values to prevent calculation errors
  const validateFormState = useCallback((state: FormState): FormState => {
    const validated = { ...state };
    
    // Ensure numeric values are positive
    validated.variants = Math.max(2, Math.round(validated.variants)); // At least 2 variants (control + 1)
    validated.baselineValue = Math.max(0.1, validated.baselineValue); // Prevent zero or negative values
    
    if (validated.testType === 'metric') {
      validated.standardDeviation = Math.max(0.1, validated.standardDeviation); // Prevent zero or negative values
      
      // If log transformation is enabled, ensure values make sense
      if (validated.logTransformation) {
        // Prevent extreme coefficient of variation that could cause numerical issues
        const cv = validated.standardDeviation / validated.baselineValue;
        if (cv > 10) {
          validated.standardDeviation = validated.baselineValue * 10;
        }
      }
    }
    
    // Ensure statistical parameters are within reasonable ranges
    validated.mde = Math.max(0.1, validated.mde); // Prevent zero or negative MDE
    validated.confidenceLevel = Math.min(Math.max(50, validated.confidenceLevel), 99.9); // Between 50% and 99.9%
    validated.power = Math.min(Math.max(50, validated.power), 99.9); // Between 50% and 99.9%
    validated.dailySamples = Math.max(1, validated.dailySamples); // At least 1 sample per day
    
    return validated;
  }, []);

  // Calculate sample size when form state changes
  const calculateResult = useCallback(() => {
    try {
      // Validate form state before calculation
      const validatedState = validateFormState(formState);
      
      // Only update form state if validation changed values
      if (JSON.stringify(validatedState) !== JSON.stringify(formState)) {
        setFormState(validatedState);
        return; // Will trigger recalculation via useEffect
      }
      
      const calculationResult = calculateSampleSize(validatedState);
      setResult(calculationResult);
    } catch (error) {
      console.error('Error calculating sample size:', error);
      setResult(null);
    }
  }, [formState, validateFormState]);

  // Calculate sample size when form state changes
  useEffect(() => {
    calculateResult();
  }, [calculateResult]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Convert string values to numbers where appropriate
    const numericFields = ['variants', 'baselineValue', 'standardDeviation', 'mde', 'confidenceLevel', 'power', 'dailySamples'];
    const newValue = numericFields.includes(name) ? parseFloat(value) || 0 : value;
    
    setFormState({
      ...formState,
      [name]: newValue,
    });
  };

  const toggleLogTransformation = () => {
    setFormState({
      ...formState,
      logTransformation: !formState.logTransformation
    });
  };

  // Copy samples per variant to clipboard
  const copySamplesPerVariant = async () => {
    if (!result) return;
    
    // Copy raw numeric value without formatting
    const success = await copyToClipboard(result.samplesPerVariant.toString());
    
    if (success) {
      setClipboardState({
        ...clipboardState,
        samplesPerVariantCopied: true
      });
    }
  };
  
  // Copy total sample size to clipboard
  const copyTotalSampleSize = async () => {
    if (!result) return;
    
    // Copy raw numeric value without formatting
    const success = await copyToClipboard(result.totalSampleSize.toString());
    
    if (success) {
      setClipboardState({
        ...clipboardState,
        totalSampleSizeCopied: true
      });
    }
  };

  // Copy share URL with test configuration to clipboard
  const copyShareUrl = async () => {
    const shareUrl = createShareUrl(formState);
    const success = await copyToClipboard(shareUrl);
    
    if (success) {
      setClipboardState({
        ...clipboardState,
        urlCopied: true
      });
    }
  };

  return {
    formState,
    setFormState,
    result,
    showStdDev,
    clipboardState,
    handleInputChange,
    toggleLogTransformation,
    copySamplesPerVariant,
    copyTotalSampleSize,
    copyShareUrl,
  };
}
